import React, { useCallback, useMemo, useRef } from "react";
import {
  DropdownToggle,
  UncontrolledDropdown,
  UncontrolledTooltip,
} from "reactstrap";
import cx from "classnames";
import { topologyConfig } from "components/Topology/configs/topologyConfig";
import { Icon, Spinner } from "components";
import { topologyColors } from "components/Topology/configs/topologyColors";
import {
  useIsFetching,
  useSelectedTopologyItemId,
  useTopologyOperation,
  useWizardActions,
} from "objs/_ConfiguratorObjs/Wizard/stores/wizardStore";
import { nodes } from "components/Topology/configs/nodesConfig";
import s from "../../topology.module.scss";

const { nodeStrokeWidth } = topologyConfig;
const { warning } = topologyColors;

export const TopologyNode = ({
  id,
  kind,
  title,
  info,
  presentation,
  dummy,
  menu,
  topologicalParentId,
  zoneIdx,
  selectedAccessories,
  dropdown,
  ...node
}) => {
  const ref = useRef(null);
  const topologyOperation = useTopologyOperation();
  const { nodeWidth, nodeHeight, nodePadding } = topologyConfig;
  const { white } = topologyColors;
  const { setSelectedTopologyItemId, setSelectedTopologyItem } =
    useWizardActions();
  const isCopyActionFetching = useIsFetching();
  const selectedTopologyItemId = useSelectedTopologyItemId();

  const isSelected = useMemo(
    () => selectedTopologyItemId === id,
    [id, selectedTopologyItemId],
  );

  const isDummy = useMemo(() => presentation === "Dummy", [presentation]);

  const { isComponentAction, isConfigAction } = useMemo(() => {
    const { kind: action, item } = topologyOperation || {};
    return {
      isComponentAction:
        (action === "cutComponent" ||
          action === "copyComponent" ||
          action === "copyConfiguration") &&
        item === id,
      isNotAction:
        (action === "cutComponent" || action === "copyComponent") &&
        item !== id,
    };
  }, [topologyOperation, id]);

  const paramsByKind = nodes[kind] || nodes.component;

  const handleClick = useCallback(
    (e) => {
      e.stopPropagation();

      setSelectedTopologyItem(node);
      setSelectedTopologyItemId(id);
    },
    [id, setSelectedTopologyItem, setSelectedTopologyItemId, node],
  );

  return (
    <div
      className="position-relative d-flex justify-content-between"
      id={`topology-item-${id}`}
      data-test-zone-idx={zoneIdx}
      data-test-dummy={dummy}
      data-test-parentid={topologicalParentId}
      data-test-presentation={presentation}
      style={{
        top: -nodeStrokeWidth / 2 + 1,
        left: -nodeStrokeWidth / 2,
        width: nodeWidth + nodeStrokeWidth - 1,
        height: nodeHeight + nodeStrokeWidth - 2,
      }}
      onClick={handleClick}
    >
      {(isComponentAction || isConfigAction) && (
        <div
          className={cx(
            "position-absolute top-0 end-0 w-100 h-100 z-1",
            s.blurOverlay,
          )}
        ></div>
      )}
      {topologyOperation?.item === id && isCopyActionFetching && (
        <div className="position-absolute top-50 end-0 translate-middle-y me-4 z-2">
          <Spinner size="xs" color={white} />
        </div>
      )}
      <div
        className="h-100"
        style={{ width: nodeHeight + nodeStrokeWidth }}
      ></div>
      <div
        className={cx("position-relative d-flex flex-grow-1 text-gray-300", {
          "bg-blue-300 text-white": isSelected,
        })}
        style={{ padding: selectedAccessories?.length ? 6 : nodePadding }}
      >
        <div
          className="d-flex flex-column"
          style={{ color: isSelected ? white : paramsByKind.title.textColor }}
        >
          <span className="font16 font-bold lh-1">{title}</span>
          <span
            className="font14 lh-1"
            style={{
              color: isDummy ? warning : "inherit",
            }}
          >
            {presentation}
          </span>
          {selectedAccessories?.length > 0 && (
            <span className="fs-sm">
              {selectedAccessories[0].currentPresentation}
            </span>
          )}
        </div>
        <div
          className="position-absolute end-0 d-flex align-items-center gap-1 mx-2"
          style={{ top: "6px" }}
        >
          {info && (
            <>
              <div className="d-flex align-items-center" ref={ref}>
                <Icon
                  className="text-white cursor-pointer"
                  tabIndex={-1}
                  iconName="circle-info"
                  prefix="far"
                />
              </div>
              <UncontrolledTooltip target={ref} placement="top">
                {info}
              </UncontrolledTooltip>
            </>
          )}

          {!!menu?.length &&
            // Sorry for magic number, but it's the only way to make it work some items has only id 10 inside and that's about add item and we have nothing to show in 3dots menu'
            menu.some((item) => item.id !== 10) && (
              <UncontrolledDropdown direction="left">
                <DropdownToggle
                  tag="div"
                  className={cx("icon-16 cursor-pointer", {
                    "user-select-none": topologyOperation,
                  })}
                >
                  <Icon
                    prefix="trox"
                    iconName="ellipsis-vertical-regular"
                    className="icon-16"
                    style={{
                      color: isSelected ? white : paramsByKind.title.textColor,
                    }}
                  />
                </DropdownToggle>
                {dropdown}
              </UncontrolledDropdown>
            )}
        </div>
      </div>
    </div>
  );
};
